import { isNonNaNNumber, validateObject } from '@iotv/iotv-v3-types';
import { createContext } from 'react';
// Provides the non-static  payload defintion values - ie constructs the object that will be used to derive the payload for the downlink
const debug = process.env.REACT_APP_DEBUG && true;
/**
 * Factory to create a generic CommonControlReducer context and reducer for any device type.
 *
 * @template StateType - The main state type (e.g., ValveActDownlinkControlObjectType)
 * @template SupportedKeys - The union of supported keys for the reducer
 * @template OptionsMapType - The type for the options map (e.g., DeviceOptionsMap)
 * @template RangeLimitsType - The type for the range limits object
 *
 * @param params - Factory parameters
 *   - defaults: Default values for the state
 *   - supportedKeysNumerical: Keys that are numbers
 *   - supportedKeysMap: Keys that are maps
 *   - isDeviceOptionsMap: Type guard for options map
 *   - rangeLimits: Range limits object
 *   - storageKey: Optional key for local storage (if needed)
 *
 * @returns { reducer, context, types }
 */
export function createGenericControlReducerContext(params) {
    console.log('createGenericControlReducerContext called with params:', params);
    const isUpdateValueAction = (x) => {
        const c = x;
        return (!!c &&
            c.type === 'UpdateValue' &&
            typeof c.update.key === 'string' &&
            params.supportedKeysNumerical.includes(c.update.key) &&
            typeof params.defaults[c.update.key] === 'number' &&
            isNonNaNNumber(c.update.v));
    };
    const isUpdateMapAction = (x) => {
        const c = x;
        return (!!c &&
            c.type === 'UpdateMap' &&
            typeof c.update.key === 'string' &&
            params.supportedKeysMap.includes(c.update.key) &&
            typeof params.defaults[c.update.key] === 'object' &&
            typeof c.update.v === 'object');
    };
    const isTogglePresenceAction = (x) => {
        const c = x;
        return !!c && c.type === 'TogglePresence' && typeof c.update.key === 'string';
    };
    const isClearDownlinkObjectAction = (x) => {
        const c = x;
        return !!c && c.type === 'ClearDownlinkObject' && c.update === null;
    };
    const originalValueSettings = Object.fromEntries(Object.entries(params.defaults).map(([k, v]) => [k, { value: v, include: false }]));
    const defaultState = {
        payloadRequestObject: {},
        valueSettings: originalValueSettings,
        validationMap: validateObject(params.defaults, params.rangeLimits ?? {}),
    };
    const reducer = (prev, action) => {
        debug && console.log(`INFO: GenericControlReducer action`, action);
        const update = { ...prev };
        if (isUpdateValueAction(action)) {
            const { key, v } = action.update;
            const applicableMetadata = params.rangeLimits[key];
            if (applicableMetadata?.attributeType === 'number') {
                const { range: { min, max }, } = applicableMetadata;
                if (!(v < min) && !(v > max)) {
                    //don't ask
                    ;
                    update.valueSettings[key].value = v;
                }
                else {
                    debug && console.log(`WARN: GenericControlReducer received out-of-range value for key ${String(key)}: ${v} (expected between ${min} and ${max})`);
                }
            }
            else {
                ;
                update.valueSettings[key].value = v;
            }
        }
        else if (isUpdateMapAction(action)) {
            const { key, v } = action.update;
            if (params.isDeviceOptionsMap(v)) {
                // CHECK was this for bitmap, now same for map
                ;
                update.valueSettings[key].value = v;
            }
            else {
                const subRangeLimits = params.rangeLimits[key];
                if (subRangeLimits) {
                    const subValidationMap = validateObject(v, subRangeLimits);
                    console.log('VALIDATE OBJECT RESULT', subValidationMap);
                    const objectIsValid = Object.values(subValidationMap).every((result) => result && result.isValid);
                    if (objectIsValid) {
                        ;
                        update.valueSettings[key].value = v;
                    }
                    else {
                        debug && console.log(`WARN: GenericControlReducer received invalid map for key ${String(key)}:`, v, 'Validation result:', subValidationMap);
                    }
                }
            }
        }
        else if (isTogglePresenceAction(action)) {
            const { key } = action.update;
            update.valueSettings[key].include = !update.valueSettings[key].include;
            debug && console.log(`TEMP: GenericControlReducer TogglePresence`, action, update.valueSettings[key]);
        }
        else if (isClearDownlinkObjectAction(action)) {
            update.payloadRequestObject = {};
            update.valueSettings = Object.fromEntries(Object.entries(update.valueSettings).map(([k, v]) => [k, { ...v, include: false }]));
        }
        else {
            debug && console.log(`WARN: GenericControlReducer received unknown action`, action);
        }
        const fullValidation = validateObject(update.payloadRequestObject, params.rangeLimits ?? {});
        console.log('FULL VALIDATION RESULT', fullValidation);
        update.validationMap = fullValidation;
        update.payloadRequestObject = Object.fromEntries(Object.entries(update.valueSettings)
            .filter(([, v]) => v.include === true)
            .map(([k, v]) => [k, v.value]));
        return update;
    };
    const Context = createContext({
        state: defaultState,
        dispatch: (action) => {
            throw new Error(`Don't use uninitialized dispatch for CommonControlContext`);
        },
    });
    return {
        reducer,
        Context,
        defaultState,
        types: {
        // These are type aliases, not values. Document for user reference only.
        // Use: typeof factory.types.UpdateValueAction, etc.
        },
    };
}
